/* ============================================================
   US-Parts24 · R485 — Bewegungskern für JEDE Seite.

   Warum es das gibt: Bis R484 lag die Leistungswache (Gerätestufe +
   Bildratenmessung) IN usp-diagnose.js — und die wird nur von
   `usparts-diagnose-rail` geladen, also nur auf der Startseite.
   usp-smooth.js liest aber `html.dataset.uspLeistung` auf JEDER Seite.
   Ergebnis, gemessen am 17.09.: Auf der Weiterleitungsseite war das
   Attribut nie gesetzt, die Wache also aus — Lenis lief dort ohne
   jede Rückfallebene. Genau der Fall, den Marcel mit „Wenn Gerät
   weniger Leistung, zurückschalten" gemeint hat.

   Jetzt: EIN Kern, der auf jeder Seite
     1. die Gerätestufe setzt (sparsam / mittel / voll),
     2. GSAP + ScrollTrigger bei Bedarf EINMAL nachlädt und korrekt
        an den echten Scroller hängt (`.page-wrapper` ab 990 px),
     3. die scroll-gekoppelten Effekte aus data-Attributen verdrahtet.

   Abgrenzung — hier wird nichts doppelt gebaut:
     · usparts.js  = Eintritts-Reveals (IntersectionObserver, an/aus)
     · usp-smooth.js = Lenis, eine Instanz, `window.uspSmooth`
     · usp-motion.js = alles SCROLL-GEKOPPELTE (läuft mit dem Finger mit)
     · usp-diagnose.js = nur noch der Prüflauf + die Schiene

   Grundregel: Fällt dieses Modul aus, sieht die Seite vollständig aus.
   Kein Effekt versteckt Inhalt — er bewegt nur, was ohnehin da ist.
   ============================================================ */

import { getScrollContainer, scrollContainerMediaQuery } from '@theme/scroll-container';

const html = document.documentElement;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const inEditor = Boolean(window.Shopify && window.Shopify.designMode);

/* ------------------------------------------------------------
   1. Leistungswache — aus usp-diagnose.js hierher gezogen
   ------------------------------------------------------------ */

function stufeSchaetzen() {
  const n = navigator;
  const verb = n.connection || {};
  const kerne = n.hardwareConcurrency || 0; // 0 = unbekannt
  const speicher = n.deviceMemory || 0; // 0 = unbekannt (Safari meldet nie)
  const sparmodus = Boolean(verb.saveData);
  const langsam = /^(slow-2g|2g)$/.test(verb.effectiveType || '');

  /* ⚠️ Unbekannt ist nicht „klein". Safari auf dem iPhone meldet keinen
     Speicher; mit `|| 4` als Standard landete jedes iPhone in „mittel"
     und war nach EINER schlechten Messung in „sparsam". Nur harte
     Signale drücken nach unten. */
  if (sparmodus || langsam) return 'sparsam';
  if ((speicher && speicher <= 2) || (kerne && kerne <= 2)) return 'sparsam';
  if (speicher && speicher <= 4 && kerne && kerne <= 4) return 'mittel';
  return 'voll';
}

const zustand = {
  stufe: reduceMotion.matches ? 'sparsam' : stufeSchaetzen(),
  fps: null,
  hoerer: new Set(),
  gsapVersprechen: null,
};

function stufeSetzen(neu) {
  if (zustand.stufe === neu) return;
  zustand.stufe = neu;
  html.dataset.uspLeistung = neu;
  zustand.hoerer.forEach((cb) => {
    try {
      cb(neu, zustand.fps);
    } catch (_) {
      /* ein kaputter Hörer darf die anderen nicht mitreißen */
    }
  });
  document.dispatchEvent(new CustomEvent('usp:leistung', { detail: { stufe: neu, fps: zustand.fps } }));
}

html.dataset.uspLeistung = zustand.stufe;

/** Bildrate kurz messen; unter 30 fps eine Stufe runter. Läuft nur einmal. */
function bildrateWachen() {
  if (zustand.stufe === 'sparsam') return;
  /* Erst messen, wenn die Seite fertig geladen ist und sich beruhigt hat.
     Während Bilder und Schriften kommen, ruckelt JEDES Gerät — das wäre
     ein falscher Abstieg beim Start. */
  const anstossen = () => setTimeout(messen, 1200);
  if (document.readyState === 'complete') anstossen();
  else window.addEventListener('load', anstossen, { once: true });

  function messen() {
    /* Verdeckter Tab liefert keine Bilder — dann warten, bis er sichtbar
       wird, statt nie zu messen. (Lehre vom 15.09.: ein eingefrorener
       Wert ist von einem falschen Wert nicht zu unterscheiden.) */
    if (document.visibilityState !== 'visible') {
      document.addEventListener('visibilitychange', () => setTimeout(messen, 800), { once: true });
      return;
    }
    let bilder = 0;
    let gescrollt = false;
    const beimScrollen = () => {
      gescrollt = true;
    };
    document.addEventListener('scroll', beimScrollen, { passive: true, capture: true });
    const t0 = performance.now();
    const tick = () => {
      bilder++;
      const dt = performance.now() - t0;
      if (dt < 1500) return requestAnimationFrame(tick);
      document.removeEventListener('scroll', beimScrollen, { capture: true });
      const fps = (bilder / dt) * 1000;
      zustand.fps = Math.round(fps);
      /* Während der Nutzer scrollt, ist die Bildrate KEIN Maß fürs Gerät.
         Und aus einer Messung geht es höchstens auf „mittel", nie auf
         „sparsam" — das ist die Stufe, in der die Seite ihren Charakter
         verliert, und die darf nur ein hartes Signal auslösen. */
      if (gescrollt) {
        setTimeout(messen, 2500);
        return;
      }
      if (fps < 30 && zustand.stufe === 'voll') stufeSetzen('mittel');
    };
    requestAnimationFrame(tick);
  }
}

/* ------------------------------------------------------------
   2. Scroller + Bibliotheken
   ------------------------------------------------------------ */

/** Der Scroller ist ab 990 px `.page-wrapper`, darunter das Fenster.
    Diese eine Zeile war 2026 schon dreimal die Ursache toter Effekte. */
function scrollerLesen() {
  const desktop = scrollContainerMediaQuery.matches;
  const container = desktop ? getScrollContainer() : null;
  return {
    desktop,
    container,
    scroller: desktop && container ? container : window,
    ereignisZiel: desktop && container ? container : document,
  };
}

function skript(src) {
  return new Promise((resolve, reject) => {
    const schon = document.querySelector(`script[data-usp-lib="${src}"]`);
    if (schon) {
      if (schon.dataset.fertig === 'ja') return resolve();
      schon.addEventListener('load', () => resolve(), { once: true });
      schon.addEventListener('error', () => reject(new Error(src)), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.dataset.uspLib = src;
    s.onload = () => {
      s.dataset.fertig = 'ja';
      resolve();
    };
    s.onerror = () => reject(new Error(`konnte nicht laden: ${src}`));
    document.head.appendChild(s);
  });
}

/** GSAP + ScrollTrigger EINMAL laden und korrekt verdrahten.
    Liefert null, wenn Bewegung hier nichts zu suchen hat. */
function gsapHolen() {
  if (zustand.gsapVersprechen) return zustand.gsapVersprechen;
  zustand.gsapVersprechen = (async () => {
    if (inEditor || reduceMotion.matches || zustand.stufe === 'sparsam') return null;
    const wurzel = document.querySelector('[data-usp-motion-assets]');
    const pfade = (wurzel && wurzel.dataset) || {};
    if (!pfade.gsap || !pfade.scrolltrigger) return null;
    try {
      if (!window.gsap) await skript(pfade.gsap);
      if (!window.ScrollTrigger) await skript(pfade.scrolltrigger);
    } catch (_) {
      return null;
    }
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger) return null;
    gsap.registerPlugin(ScrollTrigger);

    const lage = scrollerLesen();

    /* Lenis bewegt scrollTop nativ — ScrollTrigger erfährt davon nur über
       ein gewöhnliches scroll-Ereignis. Ohne diesen Takt steht progress
       auf 0, obwohl der Scrollstand längst bei 8500 liegt (R404). */
    let angefordert = false;
    const takt = () => {
      if (angefordert) return;
      angefordert = true;
      requestAnimationFrame(() => {
        angefordert = false;
        ScrollTrigger.update();
      });
    };
    lage.ereignisZiel.addEventListener('scroll', takt, { passive: true });

    /* ScrollTrigger schiebt beim Anheften Platz ein (pinSpacing) und ändert
       damit die Seitenhöhe, ohne dass ein einzelnes Element seine Größe
       ändert — ein ResizeObserver sieht das nicht. Deshalb sagen wir es
       Lenis ausdrücklich, sonst bleibt seine Scrollgrenze zu klein und der
       Footer ist nicht erreichbar (R485). */
    const meldeRefresh = () => document.dispatchEvent(new CustomEvent('usp:motion-refresh'));
    ScrollTrigger.addEventListener('refresh', meldeRefresh);

    document.addEventListener('usp:smooth-ready', () => ScrollTrigger.refresh());
    document.addEventListener('usp:smooth-off', () => ScrollTrigger.refresh());
    window.addEventListener('load', () => {
      ScrollTrigger.refresh();
      meldeRefresh();
    }, { once: true });
    scrollContainerMediaQuery.addEventListener('change', () => ScrollTrigger.refresh());

    return { gsap, ScrollTrigger, ...lage };
  })();
  return zustand.gsapVersprechen;
}

/* ------------------------------------------------------------
   3. Die Effekte — alle über data-Attribute, alle mit demselben Takt
   ------------------------------------------------------------ */

const zahl = (v, standard) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : standard;
};

/** [data-usp-parallax="0.18"] — das Element driftet gegen die Scrollrichtung.
    Bewusst NUR translate: alles andere (Filter, Größe) kostet ein Layout.

    ⚠️ Die Drift läuft SYMMETRISCH um die Ruhelage (−weg/2 … +weg/2), nicht
    von 0 nach unten. Einseitig verschoben reißt am oberen Rand eine Lücke
    auf, sobald das Bild die Fläche exakt ausfüllt. Dazu die Klasse
    `is-usp-parallax`, die das Element in CSS übergroß macht — erst beides
    zusammen ergibt einen Effekt ohne sichtbare Kante. */
function parallaxe({ gsap, ScrollTrigger, scroller }) {
  /* Zwei Schreibweisen, damit Liquid nichts Riskantes tun muss:
     a) das Element selbst trägt [data-usp-parallax="0.14"]
     b) der Rahmen trägt [data-usp-parallax-child=".usp-ph__img"] und
        benennt sein Kind. Nötig, weil `image_tag` keine Attribute mit
        Bindestrich zuverlässig durchreicht — und ein Liquid-Parserfehler
        im Seiten-Hero würde JEDE Unterseite mitnehmen. */
  document.querySelectorAll('[data-usp-parallax-child]').forEach((rahmen) => {
    const kind = rahmen.querySelector(rahmen.dataset.uspParallaxChild);
    if (kind && !kind.hasAttribute('data-usp-parallax')) {
      kind.setAttribute('data-usp-parallax', rahmen.dataset.uspParallaxKraft || '0.14');
    }
  });

  const els = document.querySelectorAll('[data-usp-parallax]');
  els.forEach((el) => {
    const kraft = Math.max(0, Math.min(0.4, zahl(el.dataset.uspParallax, 0.15)));
    if (!kraft) return;
    const rahmen = el.closest('[data-usp-parallax-scope]') || el.parentElement || el;
    /* Der Weg bemisst sich am RAHMEN, nicht am Element: ein 2400 px hohes
       Bild in einem 420 px hohen Hero soll 420 × Kraft wandern, nicht 2400. */
    const weg = () => Math.round((rahmen.offsetHeight || el.offsetHeight) * kraft);
    el.classList.add('is-usp-parallax');
    el.style.setProperty('--usp-parallax-kraft', String(kraft));
    gsap.fromTo(
      el,
      { y: () => -weg() / 2 },
      {
        y: () => weg() / 2,
        ease: 'none',
        force3D: true,
        scrollTrigger: {
          trigger: rahmen,
          scroller,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.5,
          invalidateOnRefresh: true,
        },
      }
    );
  });
  if (els.length) ScrollTrigger.refresh();
}

/** [data-usp-scrub] — die Sektion zieht sich beim Hereinkommen zusammen
    auf ihre Ruhelage. Anders als der Reveal in usparts.js läuft das MIT
    dem Finger, nicht als einmalige Abspielung.
    ⚠️ Startwert wird per JS gesetzt, nie per CSS — sonst wäre der Inhalt
    ohne JavaScript unsichtbar. */
function scrubEintritt({ gsap, scroller }) {
  document.querySelectorAll('[data-usp-scrub]').forEach((el) => {
    const kinder = el.dataset.uspScrub
      ? Array.from(el.querySelectorAll(el.dataset.uspScrub))
      : [el];
    if (!kinder.length) return;
    gsap.fromTo(
      kinder,
      { y: 26, opacity: 0.35 },
      {
        y: 0,
        opacity: 1,
        ease: 'none',
        stagger: 0.06,
        scrollTrigger: {
          trigger: el,
          scroller,
          start: 'top 92%',
          end: 'top 55%',
          scrub: 0.6,
          invalidateOnRefresh: true,
        },
      }
    );
  });
}

/** [data-usp-velocity] — die Scrollgeschwindigkeit wird zu `--usp-vel`
    (−1 … 1). Das ist der eine Effekt, den es OHNE Lenis nicht gibt:
    natives Scrollen liefert keine geglättete Geschwindigkeit.
    Verwendung: der Marken-Ticker zieht mit, wenn man schnell scrollt. */
function geschwindigkeit() {
  if (zustand.velGebunden) return; // usp:smooth-ready kann mehrfach kommen
  const els = Array.from(document.querySelectorAll('[data-usp-velocity]'));
  if (!els.length) return;
  const lenis = window.uspSmooth && window.uspSmooth.lenis;
  /* Lenis lädt seine Bibliothek selbst nach — beim ersten Durchlauf ist
     die Instanz in aller Regel noch nicht da. Dann NICHT aufgeben,
     sondern auf das Fertig-Signal warten. (Ohne das lief der Effekt nur,
     wenn die Bibliothek zufällig aus dem Cache kam.) */
  if (!lenis || typeof lenis.on !== 'function') {
    document.addEventListener('usp:smooth-ready', () => geschwindigkeit(), { once: true });
    return;
  }

  zustand.velGebunden = true;
  let letzte = 0;
  let laeuft = false;
  /* Der Teiler ist GEMESSEN, nicht geraten (17.09., Playwright, 1440 px,
     zwölf Mausrad-Schritte à 100 px): Lenis meldet im Median 4,2, im
     90. Perzentil 13,3 und in der Spitze 125,6. Mit 25 ergibt normales
     Scrollen rund 0,5 (≈ 1,9° Neigung) und eine schnelle Wischbewegung
     läuft in die Begrenzung. Mit dem ersten Entwurf (Teiler 40) waren es
     bei p90 nur 1,2° — sichtbar erst, wenn man danach sucht. */
  const TEILER = 25;
  const anwenden = () => {
    laeuft = false;
    els.forEach((el) => {
      const max = zahl(el.dataset.uspVelocity, 1) || 1;
      const v = Math.max(-1, Math.min(1, letzte / TEILER)) * max;
      el.style.setProperty('--usp-vel', v.toFixed(3));
    });
  };
  lenis.on('scroll', (e) => {
    letzte = typeof e.velocity === 'number' ? e.velocity : 0;
    if (laeuft) return;
    laeuft = true;
    requestAnimationFrame(anwenden);
  });
  /* Kommt die Bewegung zur Ruhe, muss der Wert zurück auf 0 — sonst
     bliebe der Ticker dauerhaft schräg stehen. */
  setInterval(() => {
    if (Math.abs(letzte) < 0.01) return;
    letzte *= 0.85;
    if (Math.abs(letzte) < 0.01) letzte = 0;
    anwenden();
  }, 120);
}

/** [data-usp-progress] — Lesefortschritt als `--usp-progress` (0…1).
    Hängt am echten Scroller, nicht am Fenster, und misst die Lage
    zwischengespeichert (kein getBoundingClientRect je Bild). */
function fortschritt() {
  const els = Array.from(document.querySelectorAll('[data-usp-progress]'));
  if (!els.length) return;
  const lage = scrollerLesen();
  const scrollEl = lage.scroller === window ? document.documentElement : lage.container;
  if (!scrollEl) return;

  /* R508 — Die Seitenhöhe wird NICHT mehr bei jedem Bild gelesen. Gemessen
     am 18.09. (sofort nach dem Laden gescrollt, CPU 4× gedrosselt): dieser
     Balken kostete 2,4 s — scrollHeight zwingt den Browser, die ganze Seite
     neu zu vermessen, sobald ein anderes Skript im selben Bild etwas
     geschrieben hat. Jetzt: Höhe zwischengespeichert (Laden, Größe, alle 2 s),
     im Takt nur scrollTop, und geschrieben nur bei Änderung. */
  let max = 1;
  let zuletzt = -1;
  const hoeheMessen = () => { max = Math.max(1, scrollEl.scrollHeight - scrollEl.clientHeight); };
  let angefordert = false;
  const zeichnen = () => {
    angefordert = false;
    const oben = lage.scroller === window ? window.scrollY || scrollEl.scrollTop : scrollEl.scrollTop;
    const anteil = Math.round(Math.max(0, Math.min(1, oben / max)) * 1000) / 1000;
    if (anteil === zuletzt) return;
    zuletzt = anteil;
    els.forEach((el) => el.style.setProperty('--usp-progress', anteil.toFixed(3)));
  };
  const takt = () => {
    if (angefordert) return;
    angefordert = true;
    requestAnimationFrame(zeichnen);
  };
  lage.ereignisZiel.addEventListener('scroll', takt, { passive: true });
  window.addEventListener('resize', () => { hoeheMessen(); takt(); }, { passive: true });
  window.addEventListener('load', () => { hoeheMessen(); takt(); }, { once: true });
  document.addEventListener('usp:motion-refresh', () => { hoeheMessen(); takt(); });
  setInterval(hoeheMessen, 2000);
  hoeheMessen();
  zeichnen();
}

/* ------------------------------------------------------------
   4. Start
   ------------------------------------------------------------ */

async function start() {
  bildrateWachen();

  /* Der Fortschrittsbalken braucht kein GSAP und keine Bewegungsfreigabe —
     er ist eine Anzeige, keine Animation. Bei Bewegungsreduzierung bleibt
     er trotzdem nützlich. */
  fortschritt();

  if (inEditor || reduceMotion.matches) return;

  /* Die Geschwindigkeits-Kopplung hängt an Lenis, nicht an GSAP —
     sie kostet nichts und läuft auch auf Seiten ohne Bibliothek. */
  geschwindigkeit();

  /* ⚠️ GSAP + ScrollTrigger sind zusammen rund 52 KB gzip. Sie werden
     NUR geladen, wenn diese Seite auch wirklich etwas scroll-Gekoppeltes
     zeigt. Sonst zahlt jede Produkt- und Sammlungsseite für einen Effekt,
     den sie nie darstellt. */
  const brauchtGsap = document.querySelector('[data-usp-parallax], [data-usp-parallax-child], [data-usp-scrub]');
  if (!brauchtGsap) return;

  const kit = await gsapHolen();
  if (!kit) return;

  parallaxe(kit);
  scrubEintritt(kit);

  /* Sinkt die Stufe später auf „mittel", nimmt usp-smooth Lenis weg —
     die scroll-gekoppelten Effekte laufen dann nativ weiter, nur ohne
     Glättung. Nichts verschwindet. */
  zustand.hoerer.add((neu) => {
    if (neu !== 'voll' && window.uspSmooth) window.uspSmooth.aus();
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  });
}

window.uspMotion = {
  get stufe() {
    return zustand.stufe;
  },
  get fps() {
    return zustand.fps;
  },
  stufeSetzen,
  beiStufe: (cb) => {
    zustand.hoerer.add(cb);
    return () => zustand.hoerer.delete(cb);
  },
  gsap: gsapHolen,
  scroller: scrollerLesen,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
